/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { ChipSelectMulti } from '@/shared/ui/components/form/ChipSelect';
import { Select, Text } from '@/shared/ui';
import { Field } from './FieldBase';
import { PROPERTY_CATEGORIES, TRANSACTION_TYPES, getCategoryLabel, getTransactionLabel } from './schema';
import type { PropertyFormValues } from './schema';

interface CategoryTransactionSectionProps {
  form: UseFormReturn<PropertyFormValues>;
}


/**
 * Renders the category and transaction-type section of the property drawer form.
 * @returns The category/transaction section element.
 */
export function CategoryTransactionSection({ form }: CategoryTransactionSectionProps) {
  const { t } = useTranslation('features/properties');
  const errors = form.formState.errors;
  const categoryOptions = PROPERTY_CATEGORIES.map(c => ({ value: c, label: getCategoryLabel(c, t) }));
  // Optional (plan §12.3): no asterisk, no minimum.
  const transactionOptions = TRANSACTION_TYPES.map(value => ({ value, label: getTransactionLabel(value, t) }));

  return (
    <FormSection title={t('propertyDrawer.categoryTransaction.title')}>
      <Controller
        name="category"
        control={form.control}
        render={({ field }) => (
          <Field label={t('propertyDrawer.categoryTransaction.propertyType')} error={errors.category?.message} required>
            <Select
              data={categoryOptions}
              value={field.value || null}
              onChange={(v: string | string[] | null) => {
                const next = Array.isArray(v) ? v[0] : v;
                field.onChange(next ?? '');
              }}
              placeholder="Selecione..."
            />
          </Field>
        )}
      />
      <Controller
        name="transaction_type"
        control={form.control}
        render={({ field }) => (
          <ChipSelectMulti
            label={t('propertyDrawer.categoryTransaction.purpose')}
            options={transactionOptions}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      {errors.transaction_type && (
        <Text as="span" style={{ fontSize: 11, color: 'var(--status-error)' }}>
          {errors.transaction_type.message}
        </Text>
      )}
    </FormSection>
  );
}
